import * as React from 'react';
import {useState, useCallback, useEffect} from 'react';
import { useDispatch } from 'react-redux';
import { loading } from '@redux/actions';
import { ILoadPaymentMethods } from '../interfaces';
import { ListGroup, ListItem, ListItemDescription } from "telstra-ui/components/list/List";
import { Label } from 'telstra-ui/components/label/Label';
import { Divider } from 'telstra-ui/components/divider/Divider';
import { Button } from "telstra-ui/components/button/Button";
import { Alert } from 'telstra-ui/components/alert/Alert';
import { NavLink } from 'react-router-dom';
import { GenericAlert } from 'telstra-ui/components/generic-alert/GenericAlert';
import { cardType } from '@utils/get-payment-icon';
import * as moment from 'moment';
import CreditCardForm from '../../../components/credit-card-form';
import '../style.less';
import { closeCreditCardForm } from '@telstra-connect/billing/methods/close-credit-card-form';
import { editPaymentMethodDetails } from '@telstra-connect/billing/methods/edit-payment-methods';
import { isCardExpired } from '@utils/common-functions';
import { AlertModalComponent } from '@applications/billing/components/warning-component/alert-modal';
import { DeletePaymentUIModelBody} from '@applications/billing/components/delete-payment-method';
import { deletePaymentMethod } from '@telstra-connect/billing/methods/delete-payment-methods';
import { disabledDeleteButton } from '@telstra-connect/billing/methods/disabled-delete-button';
import global from '@applications/global';

interface IAlertModalProps {
    bodyContent?: any;
    headerContent?: string;
    when?: boolean;
    buttonLabel?: string;
    onConfirmClick?: () => void;
    onCancelClick?: () => void;
}

export const ShowPaymentMethodList = (props: ILoadPaymentMethods) => {
    const newErrorCodeFFLastPMethod = global.applications.Billing.TEDIG5906LastPaymentMethodNewErrorCode || false;
    const [selectedTid, setSelectedTid] = useState('');
    const [deleteAtemptedTIDs, setDeleteAtemptedTIDs] = useState({});
    const [alertProps, setAlertProps] = useState<IAlertModalProps | null>(null);
    const [selectedDeleteTid, setselectDeleteId] = useState('');
    const b2bUid = props.b2bUid;
    const dispatch = useDispatch();
    const todayDate = new Date();
    const currentDate = moment(todayDate, "MM-YYYY");

    const checkDate = (month, year) => {
        const cardExpiryDate = (month + '-' + year)
        const cardExpDate = moment(cardExpiryDate, "MM-YYYY");
        const monthDiff = cardExpDate.month() - currentDate.month() +
        (12 * (cardExpDate.year() - currentDate.year()));
        if (month !== 'undefined') {
            return currentDate.isAfter(cardExpDate) || currentDate.isSame(cardExpDate) || monthDiff === 1 ? true : false;
        }
    }
    const isShowCreditCardForm = useCallback((tid: string) => {
        dispatch(disabledDeleteButton({isShowInlineMsg: false, errCode: '', typeOfMethods: ''}));
        sessionStorage.setItem('TC:Billing:Tid', tid);
        dispatch(closeCreditCardForm(true));
        setSelectedTid(tid);
    }, [selectedTid]);

    const onDeleteBtnClick = useCallback((tid, last4, cType, expYear, expMonth) => {
        dispatch(disabledDeleteButton({isShowInlineMsg: false, errCode: '422'}));
        setselectDeleteId(tid);
        setAlertProps({
            bodyContent: <DeletePaymentUIModelBody
                tid={tid} last4={last4}
                cardName={cardType(cType)?.Name}
                cardIcon={cardType(cType)?.icon}
                expireyYear={expYear}
                expiryMonth={expMonth}/>,
            headerContent: 'Delete payment method',
            when: true,
            buttonLabel: 'Delete'
        })
    }, [selectedDeleteTid]);

    const onCancelClick = useCallback(() => {
        setAlertProps({when: false});
    }, []);

    const onConfirmClick = useCallback(() => {
        dispatch(loading(true))
        setAlertProps({when: false});
        dispatch(deletePaymentMethod(props.cidn, props.accountId, selectedDeleteTid, b2bUid));
    }, [selectedDeleteTid]);


    const callPaymentApi = useCallback((tokenPayload) => {
        const selectedTidArgs = sessionStorage.getItem('TC:Billing:Tid') ? sessionStorage.getItem('TC:Billing:Tid') : '';
        dispatch(loading(true))
        if (tokenPayload) {
            const body = {
                b2bUid,
                paymentMethod: {
                    type: "CREDIT_CARD",
                    nonce: tokenPayload.payload.nonce,
                    deviceData: JSON.stringify({
                        device_session_id: tokenPayload.deviceData.device_session_id,
                        fraud_merchant_id: tokenPayload.deviceData.fraud_merchant_id ? tokenPayload.deviceData.fraud_merchant_id : "",
                        correlation_id: tokenPayload.deviceData.correlation_id,
                    })
                }
            }
            dispatch(editPaymentMethodDetails(body, props.accountId, props.cidn, selectedTidArgs));
            dispatch(closeCreditCardForm(false));
        }
    }, [])
    const onCancel = useCallback(() => {
        dispatch(closeCreditCardForm(false));
    }, []);

    useEffect(() => {
        if (props.disabledDeleteIcon?.isShowInlineMsg) {
            const tempData = {...deleteAtemptedTIDs}
            tempData[selectedDeleteTid] = props.disabledDeleteIcon;
            setDeleteAtemptedTIDs(tempData)
        }
    }, [props.disabledDeleteIcon]);

    const enabledDeleteBtn = useCallback((tid) => {
        const tempData = {...deleteAtemptedTIDs}
        delete tempData[tid];
        setDeleteAtemptedTIDs(tempData);
        dispatch(disabledDeleteButton({isShowInlineMsg: false, errCode: '422'}));
    }, [dispatch, deleteAtemptedTIDs]);

    return(
        <React.Fragment>
            <Divider className={'tl-light-grey'}/>
            { props?.paymentMethodList?.map((list) => {
                const errorDetails = deleteAtemptedTIDs[list.tid]
                const expired = list.type !== 'BANK_ACCOUNT' && isCardExpired(list.expiryMonth, list.expiryYear);
                const SUBMIT_BUG_LINK = '/app/support/error-report';
                const errTxt = (<>Something went wrong. Please try again. If the error persists, please&nbsp;
                    <NavLink style={{ textDecoration: 'underline', cursor : 'pointer'}}
                        to={SUBMIT_BUG_LINK} className="tc-billing-submit-feedback">
                        {'submit a bug'}</NavLink>.</>);
                const deleteErrTxt = "You cannot delete this payment method as it's being used for direct debit " +
                "payments on at least one of your Telstra accounts."
                const lastPaymentTxt = "You cannot delete your only saved payment method.";
                const errorCode = ['422209', '422210'];
                return(
                    <React.Fragment key={list.tid}>
                        <ListGroup>
                            <ListItem
                                icon={cardType(list)?.icon} itemText={cardType(list)?.Name}
                                itemDivider={false} key={list.tid}>
                                <ListItemDescription>
                                    <span className="tl-list-description-rightIcon">
                                        <span>••••</span><span>&nbsp;&nbsp;{list?.last4}</span>
                                    </span>
                                    <span className={expired ? "tl-list-description-rightIcon tc-expired-date-color" :
                                        "tl-list-description-rightIcon tc-expired-date"}>
                                        {list?.expiryMonth}{list?.expiryMonth ? `/` : ''}{list?.expiryYear?.slice(-2)}
                                    </span>
                                    { expired ?
                                        <Label className="expired-text-small-device" displayText="Expired" type="destructive" /> : ''}
                                </ListItemDescription>
                                {checkDate(list?.expiryMonth, list?.expiryYear) &&
                            (selectedTid !== list.tid || !props.isCloseCredit) ? <Button variant="primary"
                                        icon='icon-authentication-register'
                                        size="small"
                                        className={currentDate > moment.utc(list?.expiryMonth + '-' + list?.expiryYear, "MM-YYYY") ?
                                            "list-edit-icon-bt" :
                                            "list-edit-icon"}
                                        onClick={() => isShowCreditCardForm(list?.tid)}
                                        disabled={selectedTid && props.isCloseCredit ? true : false}
                                        title="icon-authentication-register"/> : <></>
                                }
                                {
                                    (selectedTid !== list.tid || !props.isCloseCredit) && <Button variant="primary"
                                        icon="icon-system-trash"
                                        size="small"
                                        className={currentDate > moment.utc(list?.expiryMonth + '-' + list?.expiryYear, "MM-YYYY") ?
                                            "list-delete-icon-bt" : "list-delete-icon"}
                                        onClick={() => onDeleteBtnClick(list?.tid, list?.last4,
                                            list, list?.expiryYear, list?.expiryMonth)}
                                        state="warning"
                                        disabled={ errorDetails && errorDetails.isShowInlineMsg ? true : false}
                                        title="icon-system-trash"/>
                                }

                            </ListItem>
                        </ListGroup>
                        {
                            selectedTid === list.tid && props.isCloseCredit && <CreditCardForm
                                token={props.paymentToken?.data?.clientToken?.value}
                                type="EDIT_PAYMENT_METHOD"
                                isStaffUser={props.isStaffUser}
                                callPaymentApi={callPaymentApi}
                                cancelClick={onCancel}/>
                        }
                        {
                            props.disabledDeleteIcon.typeOfMethods === 'delete' &&
                            selectedDeleteTid === list.tid && <Alert
                                name={'alert-inline-warning'}
                                label={errTxt}
                                icon="icon-system-alert-small"
                                labelClass="alert-err-text"/>
                        }
                        {
                            (errorDetails && errorDetails.isShowInlineMsg && errorDetails.errCode === '422') &&
                            <div className='generic-alert-padding'>
                                { newErrorCodeFFLastPMethod ?
                                    <GenericAlert
                                        name="warning-CTA"
                                        description={errorDetails.errorSubcode === '422206' ? deleteErrTxt
                                            : errorCode.includes(errorDetails.errorSubcode) ? lastPaymentTxt :  ""}
                                        showBackground={true}
                                        closeButton={true}
                                        onCloseClick={() => enabledDeleteBtn(list.tid)}/>
                                    : <GenericAlert
                                        name="warning-CTA"
                                        description={errorDetails.errorSubcode === '422206' ? deleteErrTxt
                                            : errorDetails.errorSubcode === '422205' ? lastPaymentTxt :  ""}
                                        showBackground={true}
                                        closeButton={true}
                                        onCloseClick={() => enabledDeleteBtn(list.tid)}/>
                                }
                            </div>
                        }
                        {
                            props.disabledDeleteIcon.typeOfMethods === 'edit' &&
                            selectedTid === list.tid && <Alert
                                name={'alert-inline-warning'}
                                label={errTxt}
                                icon="icon-system-alert-small"
                                labelClass="alert-err-text"/>
                        }

                        <Divider className={'tl-light-grey'}/>
                        {
                            selectedDeleteTid === list.tid && alertProps.when && <AlertModalComponent
                                bodyContent={alertProps.bodyContent}
                                headerContent={alertProps.headerContent}
                                when={alertProps.when}
                                onConfirmClick={onConfirmClick}
                                buttonLabel={alertProps.buttonLabel}
                                onCancelClick={onCancelClick}
                            />
                        }
                    </React.Fragment>)
            })}
        </React.Fragment>
    )
}

